import { gql, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import type { SubAccount } from '@interfaces/goalSubAccountTypes';
import type { TranslatedString } from '@interfaces/shared';
import { getSubAccountName } from '@interfaces/subAccount';
import type { User } from '@interfaces/user';
import { MenuItem, TextField } from '@mui/material';
import { usePermission } from '@providers/userContextProvider';
import { isNull } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const FETCH_SUB_ACCOUNTS = ({
  canReadClientLowRiskPii,
  fetchUser,
}: {
  canReadClientLowRiskPii: boolean;
  fetchUser?: boolean;
}) => gql`
  query fetchSubAccounts($filter:SubAccountQueryFilter) {
    fetchSubAccounts(
      input: {
        filter: $filter
        pagination: { perPage: 1000 }
      }
    ) {
      subAccounts {
        id
        state
        goal {
          name
        }
        account {
          id
          type
          availableCurrencies
          ${
            fetchUser
              ? `
          user {
            id
            ${canReadClientLowRiskPii ? 'firstName middleName lastName' : ''}
          }
          `
              : ''
          }
        }
        financialProduct {
          translatedName { ${TRANSLATED_STRING_FIELDS} }
        }
        statistics {
          marketValueCents
        }
        allowClientDeposits
      }
    }
  }
`;

export type SubAccountSelectData = {
  id: string;
  goal: { name: string };
  account: {
    type: string;
    id: string;
    availableCurrencies?: string[];
    user?: User;
  };
  financialProduct?: { translatedName?: TranslatedString };
  statistics: { marketValueCents: number };
};

export const subAccountHoldsCurrency = (
  subAccount: SubAccountSelectData,
  currency?: string,
): boolean => {
  if (!currency) return true;
  const available = subAccount?.account?.availableCurrencies;
  if (!available?.length) return true;
  return available.includes(currency);
};

export const SubAccountSelect = ({
  setSubAccount,
  value,
  label,
  size,
  disabledWith = null,
  readonly = false,
  forObject,
  forId,
  onlyAccountType,
  setSubAccountData,
  excludedIds,
  currency,
  disabled = false,
  selectFirst = false,
  includeAnyOption = false,
}: {
  setSubAccount: (event: any) => void;
  value: string;
  label: string;
  size?: 'small' | 'medium' | undefined;
  disabledWith?: string | null;
  readonly?: boolean;
  forObject: 'USER' | 'CLIENT_GROUP' | 'ACCOUNT';
  forId: string;
  onlyAccountType?: string;
  setSubAccountData?: (data: SubAccountSelectData) => void;
  excludedIds?: string[];
  currency?: string;
  disabled?: boolean;
  selectFirst?: boolean;
  includeAnyOption?: boolean;
}) => {
  const { t } = useTranslation(['components']);
  const canReadClientLowRiskPii = usePermission('read:client_low_risk_pii');
  const canReadAccountBasic = usePermission('read:account_basic');
  const showClientNames = forObject === 'CLIENT_GROUP';
  const [subAccounts, setSubAccounts] = useState<SubAccount[]>([]);
  const { loading } = useQuery(
    FETCH_SUB_ACCOUNTS({ canReadClientLowRiskPii, fetchUser: showClientNames }),
    {
      variables: {
        filter: {
          ...(forObject === 'USER' && { userId: forId }),
          ...(forObject === 'CLIENT_GROUP' && { clientGroupId: forId }),
          ...(forObject === 'ACCOUNT' && { accountId: forId }),
          ...(onlyAccountType && { accountType: onlyAccountType }),
        },
      },
      onCompleted: (data) => {
        const flatSubAccounts = Object.keys(data)
          .flatMap((index) => data[index].subAccounts)
          .sort((a, b) => a.account.type.localeCompare(b.account.type));
        setSubAccounts(flatSubAccounts);
      },
    },
  );

  useEffect(() => {
    if (setSubAccountData && value && subAccounts.length > 0) {
      const subAccount = subAccounts.find((s: any) => s.id === value);
      if (subAccount) {
        setSubAccountData(subAccount as SubAccountSelectData);
      }
    }
  }, [value, subAccounts]);

  const subAccountsShown = subAccounts.filter(
    (x: SubAccount) =>
      (x.state !== 'INACTIVE' || x.id === value) &&
      (!excludedIds || !excludedIds.includes(x.id)) &&
      (x.id === value ||
        subAccountHoldsCurrency(
          x as unknown as SubAccountSelectData,
          currency,
        )),
  );

  useEffect(() => {
    if (
      selectFirst &&
      !loading &&
      value === '' &&
      subAccountsShown.length > 0
    ) {
      setSubAccount({ target: { value: subAccountsShown[0].id } });
    }
  }, [selectFirst, loading, subAccounts]);

  return (
    <>
      {canReadAccountBasic ? (
        <TextField
          select
          value={loading ? '' : value}
          label={label}
          sx={{ width: '100%' }}
          onChange={(e: any) => {
            setSubAccount(e);
            if (setSubAccountData && subAccounts) {
              const subAccount = subAccounts.find(
                (s: any) => s.id === e.target.value,
              );
              setSubAccountData(subAccount as SubAccountSelectData);
            }
          }}
          size={size}
          disabled={!isNull(disabledWith) || disabled}
          InputProps={{
            readOnly: readonly,
          }}
        >
          {loading ? (
            <MenuItem>...</MenuItem>
          ) : disabledWith ? (
            <MenuItem key={value} value={value}>
              <b>{disabledWith}</b>
            </MenuItem>
          ) : (
            [
              includeAnyOption && (
                <MenuItem key='any' value='any'>
                  {t('any')}
                </MenuItem>
              ),
              subAccountsShown.map((sa: any) => (
                <MenuItem key={sa.id} value={sa.id}>
                  {getSubAccountName(sa, showClientNames)}
                </MenuItem>
              )),
            ]
          )}
        </TextField>
      ) : (
        <></>
      )}
    </>
  );
};
